import { resolveSeatLicenseType } from './portalOrganization.js'
import {
  normalizeNfhsAddonKind,
  normalizeNfhsAddonTier,
} from '@/utils/nfhsPublicationAddon.js'

/** QuickBooks product/service names — must match the production catalog exactly. */
export const QUICKBOOKS_PRODUCTS = {
  IN_PROGRESS_SEAT_UPGRADE: 'RefReps In-Progress Seat Upgrade',
  COURSE_BASIC_SEATS: 'Course Basic Seat Licenses',
  COURSE_LEGACY_SEATS: 'Course Legacy Seat Licenses',
  COURSE_PREMIUM_SEATS: 'Course Premium Seat Licenses',
  POV_BASIC_SEATS: 'POV Video Pack Basic Seat Licenses',
  POV_LEGACY_SEATS: 'POV Video Pack Legacy Seat Licenses',
  POV_PREMIUM_SEATS: 'POV Video Pack Premium Seat Licenses',
  COURSE_LEGACY_TO_PREMIUM: 'Course Legacy to Premium Seat License Upgrades',
  COURSE_LEGACY_TO_BASIC: 'Course Legacy to Basic Seat License Upgrades',
  COURSE_PREMIUM_TO_BASIC: 'Course Premium to Basic Seat License Downgrades',
  COURSE_BASIC_TO_PREMIUM: 'Course Basic to Premium Seat License Upgrades',
  POV_LEGACY_TO_POV_PREMIUM:
    'POV Video Pack Legacy to POV Video Pack Premium Seat License Upgrades',
  POV_LEGACY_TO_POV_BASIC:
    'POV Video Pack Legacy to POV Video Pack Basic Seat License Upgrades',
  POV_PREMIUM_TO_POV_BASIC:
    'POV Video Pack Premium to POV Video Pack Basic Seat License Downgrades',
  POV_BASIC_TO_POV_PREMIUM:
    'POV Video Pack Basic to POV Video Pack Premium Seat License Upgrades',
  POV_LEGACY_TO_COURSE_PREMIUM:
    'POV Video Pack Legacy to Course Premium Seat License Upgrades',
  POV_BASIC_TO_COURSE_PREMIUM:
    'POV Video Pack Basic to Course Premium Seat License Upgrades',
  POV_BASIC_TO_COURSE_BASIC:
    'POV Video Pack Basic to Course Basic Seat License Upgrade',
  SEAT_CONVERSION_UPGRADE: 'RefReps Seat License Conversion / Upgrade',
  /** Title × learner purchase from the Publications tab (single QB product). */
  NFHS_PUBLICATIONS_ASSIGNMENT: 'NFHS Publications Assignment',
  PUBLICATIONS_TO_COURSE_BASIC:
    'Publications to Course Basic Seat License Upgrade',
  PUBLICATIONS_TO_COURSE_PREMIUM:
    'Publications to Course Premium Seat License Upgrade',
  /** Canonical individual add-on products (one kind each) — legacy seat add-on path. */
  NFHS_RULE_BOOKS: 'RefReps Add-On for NFHS Digital Rule Books',
  NFHS_CASEBOOKS: 'RefReps Add-On for NFHS Digital Case Books',
  NFHS_MANUALS: 'RefReps Add-On for NFHS Digital Officials Manuals',
  NFHS_HANDBOOKS: 'RefReps Add-On for NFHS Digital Handbooks',
  /** Legacy names — still recognized on inbound import only. */
  NFHS_DIGITAL_PUBLICATIONS: 'RefReps Add-On for NFHS Digital Publications',
  /** @deprecated Prefer NFHS_RULE_BOOKS (space in "Rule Books"). */
  NFHS_RULEBOOKS: 'RefReps Add-On for NFHS Digital Rulebooks',
  NFHS_RULE_CASEBOOKS: 'RefReps Add-On for NFHS Digital Rulebooks and Casebooks',
  NFHS_RULE_CASE_MANUALS:
    'RefReps Add-On for NFHS Digital Rulebooks, Casebooks, and Officials Manuals',
}

/** Legacy generic description — used when no add-on kind/tier is known. */
export const NFHS_ADDON_DESCRIPTION =
  'Includes the ability to assign NFHS Digital publications'

/** QuickBooks line descriptions keyed by individual NFHS add-on kind. */
export const NFHS_ADDON_DESCRIPTIONS = {
  rulebook:
    'Includes the ability to assign NFHS Digital rule books to learners for each included seat.',
  casebook:
    'Includes the ability to assign NFHS Digital case books to learners for each included seat.',
  manual:
    'Includes the ability to assign NFHS Digital officials manuals to learners for each included seat.',
  handbook:
    'Includes the ability to assign NFHS Digital handbooks to learners for each included seat.',
}

/** @deprecated Legacy stacked-package descriptions — kept for combo-tier fallback. */
const NFHS_ADDON_DESCRIPTIONS_BY_TIER = {
  rulebooks:
    'Includes the ability to assign NFHS Digital rulebooks to learners for each included seat.',
  rule_casebooks:
    'Includes the ability to assign NFHS Digital rulebooks and casebooks to learners for each included seat.',
  rule_case_manuals:
    'Includes the ability to assign NFHS Digital rulebooks, casebooks, and officials manuals to learners for each included seat.',
}

const NFHS_ADDON_PRODUCTS_BY_KIND = {
  rulebook: QUICKBOOKS_PRODUCTS.NFHS_RULE_BOOKS,
  casebook: QUICKBOOKS_PRODUCTS.NFHS_CASEBOOKS,
  manual: QUICKBOOKS_PRODUCTS.NFHS_MANUALS,
  handbook: QUICKBOOKS_PRODUCTS.NFHS_HANDBOOKS,
}

/** @deprecated Legacy stacked packages — outbound only when given a legacy tier id. */
const NFHS_ADDON_PRODUCTS_BY_TIER = {
  rulebooks: QUICKBOOKS_PRODUCTS.NFHS_RULEBOOKS,
  rule_casebooks: QUICKBOOKS_PRODUCTS.NFHS_RULE_CASEBOOKS,
  rule_case_manuals: QUICKBOOKS_PRODUCTS.NFHS_RULE_CASE_MANUALS,
}

const PAID_CONVERSION_PRODUCTS = {
  'Legacy→Premium': QUICKBOOKS_PRODUCTS.COURSE_LEGACY_TO_PREMIUM,
  'Legacy→Basic': QUICKBOOKS_PRODUCTS.COURSE_LEGACY_TO_BASIC,
  'Premium→Basic': QUICKBOOKS_PRODUCTS.COURSE_PREMIUM_TO_BASIC,
  'Basic→Premium': QUICKBOOKS_PRODUCTS.COURSE_BASIC_TO_PREMIUM,
  'Supp. Legacy→Supp. Premium': QUICKBOOKS_PRODUCTS.POV_LEGACY_TO_POV_PREMIUM,
  'Supp. Legacy→Supp. Basic': QUICKBOOKS_PRODUCTS.POV_LEGACY_TO_POV_BASIC,
  'Supp. Premium→Supp. Basic': QUICKBOOKS_PRODUCTS.POV_PREMIUM_TO_POV_BASIC,
  'Supp. Basic→Supp. Premium': QUICKBOOKS_PRODUCTS.POV_BASIC_TO_POV_PREMIUM,
  'Supp. Legacy→Premium': QUICKBOOKS_PRODUCTS.POV_LEGACY_TO_COURSE_PREMIUM,
  'Supp. Basic→Premium': QUICKBOOKS_PRODUCTS.POV_BASIC_TO_COURSE_PREMIUM,
  'Supp. Basic→Basic': QUICKBOOKS_PRODUCTS.POV_BASIC_TO_COURSE_BASIC,
  'Publications→Basic': QUICKBOOKS_PRODUCTS.PUBLICATIONS_TO_COURSE_BASIC,
  'Publications→Premium': QUICKBOOKS_PRODUCTS.PUBLICATIONS_TO_COURSE_PREMIUM,
}

/** Line-item descriptions for new seat purchases (`key: seats` / `key: supplemental_packs`). */
export const SEAT_PURCHASE_DESCRIPTIONS = {
  Basic:
    'Includes one RefReps Education Course Basic Seat License per learner per sport course and one POV Video Training Pack when applicable. Instructors will receive access to all Basic Learning Activities.',
  Legacy:
    'Includes one RefReps Education Course Legacy Seat License per learner per sport course and one POV Video Training Pack when applicable. Instructors will receive access to all Basic Learning Activities.',
  Premium:
    'Includes one RefReps Education Course Premium Seat License per learner per sport course, two POV Video Training Packs per sport (when applicable), and one Sport Course Exam per learner per sport. Instructors will receive access to all Basic and Premium Learning Activities.',
  'Supp. Basic': 'Includes one RefReps POV Video Pack Basic Seat License per learner.',
  'Supp. Legacy': 'Includes one RefReps POV Video Pack Legacy Seat License per learner.',
  'Supp. Premium': 'Includes one RefReps POV Video Pack Premium Seat License per learner.',
  Publications:
    'Publications-only distribution. NFHS Digital titles are purchased per learner from the Publications tab.',
}

/**
 * Line-item descriptions for seat conversions/upgrades (`key: conversion`).
 * Keyed by `${from}→${to}` (resolved seat license types), matching PAID_CONVERSION_PRODUCTS.
 */
export const CONVERSION_DESCRIPTIONS = {
  'Basic→Premium': 'Converts one Basic Course Seat License to one Premium Course Seat License.',
  'Legacy→Premium': 'Converts one Legacy Course Seat License to one Premium Course Seat License.',
  'Legacy→Basic': 'Converts one Legacy Course Seat License to one Basic Course Seat License.',
  'Premium→Basic': 'Converts one Premium Course Seat License to one Basic Course Seat License at no charge.',
  'Supp. Basic→Basic': 'Converts one Basic POV Pack Seat License to one Course Basic Seat License.',
  'Supp. Basic→Premium': 'Converts one Basic POV Pack Seat License to one Premium Course Seat License.',
  'Supp. Basic→Supp. Premium': 'Converts one Basic POV Pack Seat License to one Premium POV Pack Seat License.',
  'Supp. Legacy→Premium': 'Converts one Legacy POV Pack Seat License to one Course Premium Seat License.',
  'Supp. Legacy→Supp. Premium': 'Converts one Legacy POV Pack Seat License to one Premium POV Pack Seat License.',
  'Supp. Legacy→Supp. Basic': 'Converts one Legacy POV Pack Seat License to one Basic POV Pack Seat License at no charge.',
  'Supp. Premium→Supp. Basic': 'Converts one Premium POV Pack Seat License to one Basic POV Pack Seat License at no charge.',
  'Publications→Basic':
    'Upgrades a Publications-only organization to one Course Basic Seat License per converted seat.',
  'Publications→Premium':
    'Upgrades a Publications-only organization to one Course Premium Seat License per converted seat.',
}

/**
 * QuickBooks product for new seat purchases (`key: seats`).
 * @param {string | undefined | null} licenseType
 */
export function quickbooksProductForSeatPurchase(licenseType) {
  const type = resolveSeatLicenseType(licenseType)
  if (type === 'Legacy') return QUICKBOOKS_PRODUCTS.COURSE_LEGACY_SEATS
  if (type === 'Basic') return QUICKBOOKS_PRODUCTS.COURSE_BASIC_SEATS
  if (type === 'Premium') return QUICKBOOKS_PRODUCTS.COURSE_PREMIUM_SEATS
  if (type === 'Supp. Legacy') return QUICKBOOKS_PRODUCTS.POV_LEGACY_SEATS
  if (type === 'Supp. Basic') return QUICKBOOKS_PRODUCTS.POV_BASIC_SEATS
  if (type === 'Supp. Premium') return QUICKBOOKS_PRODUCTS.POV_PREMIUM_SEATS
  // Publications orgs buy titles per learner; course seat purchase defaults to Basic product.
  if (type === 'Publications') return QUICKBOOKS_PRODUCTS.COURSE_BASIC_SEATS
  return QUICKBOOKS_PRODUCTS.COURSE_BASIC_SEATS
}

/**
 * Billing line-item description for new seat purchases (`key: seats`).
 * Only Course Basic / Legacy / Premium get copy; all other types return ''.
 * @param {string | undefined | null} licenseType
 */
export function descriptionForSeatPurchase(licenseType) {
  const type = resolveSeatLicenseType(licenseType)
  return SEAT_PURCHASE_DESCRIPTIONS[type] || ''
}

/**
 * No-fee Legacy conversions use the generic conversion product when the cart
 * is conversion-only. Buying new seats on that same order uses the dedicated
 * Legacy → Basic product so QuickBooks keeps the $0 line.
 * @param {string} pair `${from}→${to}`
 * @param {boolean} addingNewSeats
 */
function usesDedicatedNoFeeConversionProduct(pair, addingNewSeats) {
  if (!addingNewSeats) return false
  return pair === 'Legacy→Basic' || pair === 'Supp. Legacy→Supp. Basic'
}

/**
 * QuickBooks product for org-wide license conversions (`key: conversion`).
 * Known paid pairs use dedicated products. A free Legacy → Basic conversion
 * uses the generic product unless the cart also buys new seats.
 * @param {string | undefined | null} fromLicense
 * @param {string | undefined | null} toLicense
 * @param {{ addingNewSeats?: boolean }} [options]
 */
export function quickbooksProductForConversion(fromLicense, toLicense, options = {}) {
  const from = resolveSeatLicenseType(fromLicense)
  const to = resolveSeatLicenseType(toLicense)
  const pair = `${from}→${to}`
  const addingNewSeats = Boolean(options.addingNewSeats)
  if (usesDedicatedNoFeeConversionProduct(pair, addingNewSeats)) {
    return PAID_CONVERSION_PRODUCTS[pair]
  }
  if (pair === 'Legacy→Basic' || pair === 'Supp. Legacy→Supp. Basic') {
    return QUICKBOOKS_PRODUCTS.SEAT_CONVERSION_UPGRADE
  }
  return (
    PAID_CONVERSION_PRODUCTS[pair] ??
    QUICKBOOKS_PRODUCTS.SEAT_CONVERSION_UPGRADE
  )
}

/**
 * Billing line-item description for seat conversions/upgrades (`key: conversion`).
 * Known conversion pairs get copy; all others return ''.
 * @param {string | undefined | null} fromLicense
 * @param {string | undefined | null} toLicense
 */
export function descriptionForConversion(fromLicense, toLicense) {
  const from = resolveSeatLicenseType(fromLicense)
  const to = resolveSeatLicenseType(toLicense)
  return CONVERSION_DESCRIPTIONS[`${from}→${to}`] || ''
}

/** QuickBooks product for in-progress learner seat upgrades (`key: in_progress_seat_upgrade`). */
export function quickbooksProductForInProgressSeatUpgrade() {
  return QUICKBOOKS_PRODUCTS.IN_PROGRESS_SEAT_UPGRADE
}

/** Catalog/default description for title × learner NFHS publication purchases. */
export const NFHS_PUBLICATIONS_ASSIGNMENT_DESCRIPTION =
  'Includes the assignment of this book title to a learner.'

/**
 * QuickBooks product for Publications-tab title × learner purchases
 * (`key: nfhs_publication_assign`).
 */
export function quickbooksProductForNfhsPublicationAssign() {
  return QUICKBOOKS_PRODUCTS.NFHS_PUBLICATIONS_ASSIGNMENT
}

/**
 * QuickBooks product for NFHS publication add-ons (`key: nfhs`).
 * Prefers individual kind products; falls back to legacy combo names for stacked tiers.
 * @param {string | undefined | null} kindOrTier kind id or legacy tier id
 */
export function quickbooksProductForNfhsAddon(kindOrTier) {
  const kind = normalizeNfhsAddonKind(kindOrTier)
  if (kind && NFHS_ADDON_PRODUCTS_BY_KIND[kind]) {
    return NFHS_ADDON_PRODUCTS_BY_KIND[kind]
  }
  const tier = normalizeNfhsAddonTier(kindOrTier)
  if (tier && NFHS_ADDON_PRODUCTS_BY_TIER[tier]) {
    return NFHS_ADDON_PRODUCTS_BY_TIER[tier]
  }
  return QUICKBOOKS_PRODUCTS.NFHS_DIGITAL_PUBLICATIONS
}

/**
 * Billing line-item description for NFHS publication add-ons (`key: nfhs`).
 * @param {string | undefined | null} kindOrTier kind id or legacy tier id
 */
export function descriptionForNfhsAddon(kindOrTier) {
  const kind = normalizeNfhsAddonKind(kindOrTier)
  if (kind && NFHS_ADDON_DESCRIPTIONS[kind]) {
    return NFHS_ADDON_DESCRIPTIONS[kind]
  }
  const tier = normalizeNfhsAddonTier(kindOrTier)
  if (tier && NFHS_ADDON_DESCRIPTIONS_BY_TIER[tier]) {
    return NFHS_ADDON_DESCRIPTIONS_BY_TIER[tier]
  }
  return NFHS_ADDON_DESCRIPTION
}

/**
 * QuickBooks product for additional POV supplemental pack add-ons (`key: supplemental_packs`).
 * Uses the POV tier that matches the org seat license type.
 * @param {string | undefined | null} licenseType
 */
export function quickbooksProductForSupplementalPacksPurchase(licenseType) {
  const type = resolveSeatLicenseType(licenseType)
  if (type === 'Legacy' || type === 'Supp. Legacy') {
    return QUICKBOOKS_PRODUCTS.POV_LEGACY_SEATS
  }
  if (type === 'Premium' || type === 'Supp. Premium') {
    return QUICKBOOKS_PRODUCTS.POV_PREMIUM_SEATS
  }
  return QUICKBOOKS_PRODUCTS.POV_BASIC_SEATS
}

/**
 * Billing line-item description for POV supplemental pack add-ons (`key: supplemental_packs`).
 * Matches the POV tier used by quickbooksProductForSupplementalPacksPurchase.
 * @param {string | undefined | null} licenseType
 */
export function descriptionForSupplementalPacksPurchase(licenseType) {
  const type = resolveSeatLicenseType(licenseType)
  if (type === 'Legacy' || type === 'Supp. Legacy') {
    return SEAT_PURCHASE_DESCRIPTIONS['Supp. Legacy']
  }
  if (type === 'Premium' || type === 'Supp. Premium') {
    return SEAT_PURCHASE_DESCRIPTIONS['Supp. Premium']
  }
  return SEAT_PURCHASE_DESCRIPTIONS['Supp. Basic']
}
